/**
 * <background-frame-animation>
 *
 * Canvas-based renderer for a scroll-driven image sequence (the GSAP
 * "imageSequence" pattern). This element owns a single <canvas>, builds the
 * list of frame URLs from a Shopify Files base path, preloads the frames and
 * draws the requested frame on demand.
 *
 * It is intentionally free of any animation library. The scroll driving lives
 * in the controller (<kb-waitlist-storytelling>), which calls `seek(progress)`.
 * Keeping this element library-agnostic also makes the phase-2 video source a
 * drop-in extension.
 *
 * All tunable values live in DEFAULT_CONFIGURATION and can be overridden per
 * instance via data-* attributes set in Liquid.
 */

const DEFAULT_CONFIGURATION = {
  // Frame naming (Shopify Files): `${frameName}${separator}${index}.${format}`
  frameName: 'kb-lipstick-animation-frame',
  frameCount: 250,
  // Optional portrait set. When the mobile name is empty the desktop set is
  // reused and cover-cropped to portrait.
  frameNameMobile: '',
  frameCountMobile: 0,
  frameFormat: 'png',
  frameSeparator: '-',
  frameStartIndex: 1,
  // Zero-padding of the frame number (0 = no padding, e.g. "-7"; 4 = "-0007").
  framePad: 0,

  // Width caps (CSS px) for the image requested from the Shopify CDN. The CDN
  // negotiates webp/avif automatically via the Accept header, so we only ask
  // for a width and never force a format.
  maxRequestWidthDesktop: 1600,
  maxRequestWidthMobile: 750,
  maxDevicePixelRatio: 2,

  // Frame thinning. On constrained devices we request fewer frames to keep
  // decoded-bitmap memory within the iOS Safari budget. Progress is mapped onto
  // the loaded subset, so text and dots are unaffected.
  frameStrideDesktop: 1,
  frameStrideMobile: 2,
  lowMemoryDeviceMemoryGb: 4,
  lowMemoryFrameStride: 3,

  // Fraction of frames that must be loaded before the element reports ready.
  readyAtLoadedFraction: 0.25,

  // How many frame requests may be in flight at once. Frames are started a few
  // at a time, nearest the frame currently on screen first (frame 0 at load),
  // instead of firing all <frameCount> requests on connect. Assigning src to
  // every Image up front makes the browser round-robin hundreds of equal-
  // priority downloads, so the EARLY frames the user reaches first are starved
  // and the sequence looks like it is "waiting for every image to load" before
  // it plays. A small in-flight window keeps the connection busy while
  // guaranteeing the next frames you will actually reach arrive first.
  preloadConcurrency: 6,

  // iOS Safari refuses to draw canvases larger than ~16.7M pixels.
  maxCanvasPixels: 16_000_000,

  // Breakpoint (matches the theme) below which the portrait set is used.
  mobileBreakpointPx: 750,
};

class BackgroundFrameAnimation extends HTMLElement {
  #abortController = new AbortController();
  #configuration = { ...DEFAULT_CONFIGURATION };

  /** @type {HTMLCanvasElement | null} */
  #canvas = null;
  /** @type {CanvasRenderingContext2D | null} */
  #context = null;

  /** @type {HTMLImageElement[]} */
  #frameImages = [];
  /** @type {string[]} */
  #frameUrls = [];
  #loadedFrameCount = 0;
  // Number of frames that must decode before `ready` resolves; computed once in
  // #preloadFrames from readyAtLoadedFraction.
  #readyThreshold = 0;
  // Frame requests currently in flight. The background fill (#startNextLoad)
  // keeps this at preloadConcurrency; a priority load (the on-screen frame, via
  // seek) may push it one over briefly until the next frame finishes.
  #inFlightCount = 0;
  #currentFrameIndex = -1;
  // The frame the controller last asked to show. A loaded frame is painted only
  // if it matches this, so the first paint shows the correct frame for the
  // current scroll position (defaults to 0 = top of page) without a wrong-frame
  // flash on reload-while-scrolled.
  #pendingFrameIndex = 0;

  #canvasCssWidth = 0;
  #canvasCssHeight = 0;

  #isReady = false;
  /** @type {(value?: unknown) => void} */
  #resolveReady = () => {};
  /** @type {Promise<void>} */
  #readyPromise = new Promise((resolve) => {
    this.#resolveReady = resolve;
  });

  /** @type {MediaQueryList | null} */
  #mobileMediaQuery = null;
  #usingMobileSource = false;
  // Guards one-time setup: GSAP's pin reparents this element repeatedly, firing
  // connectedCallback many times. Initialisation must run only on the first one.
  #hasInitialised = false;

  connectedCallback() {
    // GSAP's ScrollTrigger pin reparents this element's ancestor in and out of
    // a pin-spacer on every refresh, which disconnects and reconnects this
    // element many times. The #abortController field initialiser runs only once
    // at construction, so re-arm it on every connect — otherwise the long-lived
    // listeners attached below would bind to an already-aborted signal and
    // never fire.
    this.#abortController = new AbortController();

    if (this.#hasInitialised) {
      // Reconnect: the instance state, the loaded frames and the painted canvas
      // all survive the reparent. Only the environment listeners were torn off
      // by the previous abort, so just re-attach them. Re-running initialisation
      // here would resize (and clear) the canvas and refetch every frame.
      this.#attachEnvironmentListeners();
      return;
    }

    this.#configuration = this.#readConfiguration();

    this.#canvas = this.querySelector('canvas') ?? this.appendChild(document.createElement('canvas'));
    // Transparent context so the brand background shows through until the first
    // frame has decoded (an opaque context would start black).
    this.#context = this.#canvas.getContext('2d', { alpha: true });

    this.#mobileMediaQuery = window.matchMedia(`(max-width: ${this.#configuration.mobileBreakpointPx - 1}px)`);

    this.#initialiseActiveSource();
    this.#attachEnvironmentListeners();
    this.#hasInitialised = true;
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #attachEnvironmentListeners() {
    window.addEventListener('resize', this.#handleResize, { signal: this.#abortController.signal });
    this.#mobileMediaQuery?.addEventListener('change', this.#handleSourceChange, {
      signal: this.#abortController.signal,
    });
  }

  /* ----- Public API ----- */

  /**
   * Draw the frame that corresponds to a scroll progress value.
   * @param {number} progress 0..1
   */
  seek(progress) {
    const frameCount = this.frameCount;
    if (frameCount === 0) return;

    const clampedProgress = Math.min(1, Math.max(0, progress));
    const targetFrameIndex = Math.round(clampedProgress * (frameCount - 1));
    // Record the intent so #handleFrameLoaded can paint this frame the moment it
    // decodes, even if its image has not loaded yet at this call.
    this.#pendingFrameIndex = targetFrameIndex;
    if (targetFrameIndex !== this.#currentFrameIndex) {
      this.#renderFrame(targetFrameIndex);
    }
    // Make sure the frame we now need is actually downloading. On a reload mid-
    // section the browser restores the scroll position, so the very FIRST seek
    // can land deep in the sequence (e.g. frame 400) while the background fill is
    // still on the frames near 0. Start the needed frame straight away, at
    // priority — even if that briefly puts one request over preloadConcurrency —
    // so the on-screen frame never waits behind frames the user cannot see.
    this.#ensureFrameLoading(targetFrameIndex);
  }

  /** Number of frames actually loaded (after stride thinning). */
  get frameCount() {
    return this.#frameUrls.length;
  }

  /** Resolves once enough frames have loaded for the animation to begin. */
  get ready() {
    return this.#readyPromise;
  }

  get isReady() {
    return this.#isReady;
  }

  /** Recompute the canvas backing store and redraw the current frame. */
  redraw() {
    const dimensionsChanged = this.#resizeCanvas();
    // If the backing store did not change, the current frame is still painted on
    // the canvas — re-rendering would redo identical work, and any new frame is
    // handled by seek(). Bail out so a stream of no-op resize events (the iOS
    // browser bar animating) does not repeatedly repaint.
    if (!dimensionsChanged && this.#currentFrameIndex >= 0) return;

    const frameIndexToRedraw = this.#currentFrameIndex >= 0 ? this.#currentFrameIndex : 0;
    this.#currentFrameIndex = -1;
    this.#renderFrame(frameIndexToRedraw);
  }

  /* ----- Configuration ----- */

  #readConfiguration() {
    const dataset = this.dataset;
    const readText = (value, fallback) => (value != null && value !== '' ? value : fallback);
    const readNumber = (value, fallback) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    return {
      ...DEFAULT_CONFIGURATION,
      frameName: readText(dataset.frameName, DEFAULT_CONFIGURATION.frameName),
      frameCount: readNumber(dataset.frameCount, DEFAULT_CONFIGURATION.frameCount),
      frameNameMobile: readText(dataset.frameNameMobile, DEFAULT_CONFIGURATION.frameNameMobile),
      frameCountMobile: readNumber(dataset.frameCountMobile, DEFAULT_CONFIGURATION.frameCountMobile),
      frameFormat: readText(dataset.frameFormat, DEFAULT_CONFIGURATION.frameFormat),
      frameSeparator: readText(dataset.frameSeparator, DEFAULT_CONFIGURATION.frameSeparator),
      frameStartIndex: readNumber(dataset.frameStartIndex, DEFAULT_CONFIGURATION.frameStartIndex),
      framePad: readNumber(dataset.framePad, DEFAULT_CONFIGURATION.framePad),
      maxRequestWidthDesktop: readNumber(dataset.maxFrameWidthDesktop, DEFAULT_CONFIGURATION.maxRequestWidthDesktop),
      maxRequestWidthMobile: readNumber(dataset.maxFrameWidthMobile, DEFAULT_CONFIGURATION.maxRequestWidthMobile),
      firstFrameUrl: readText(dataset.firstFrameUrl, ''),
      firstFrameUrlMobile: readText(dataset.firstFrameUrlMobile, ''),
      frameBaseUrlOverride: readText(dataset.frameBaseUrlOverride, ''),
    };
  }

  /* ----- Source selection ----- */

  #shouldUseMobileSource() {
    const hasMobileSet = this.#configuration.frameNameMobile !== '' && this.#configuration.frameCountMobile > 0;
    return hasMobileSet && Boolean(this.#mobileMediaQuery?.matches);
  }

  #selectActiveSource() {
    const useMobile = this.#shouldUseMobileSource();
    const configuration = this.#configuration;

    return {
      isMobile: useMobile,
      frameName: useMobile ? configuration.frameNameMobile : configuration.frameName,
      frameCount: useMobile ? configuration.frameCountMobile : configuration.frameCount,
      firstFrameUrl: useMobile ? configuration.firstFrameUrlMobile : configuration.firstFrameUrl,
      maxRequestWidth: useMobile ? configuration.maxRequestWidthMobile : configuration.maxRequestWidthDesktop,
      frameStride: useMobile ? configuration.frameStrideMobile : configuration.frameStrideDesktop,
    };
  }

  #initialiseActiveSource() {
    const source = this.#selectActiveSource();
    this.#usingMobileSource = source.isMobile;

    const baseUrl = this.#deriveBaseUrl(source.firstFrameUrl);
    this.#resizeCanvas();

    const requestWidth = this.#computeRequestWidth(source.maxRequestWidth);
    const frameStride = this.#computeFrameStride(source.frameStride);

    this.#frameUrls = baseUrl ? this.#buildFrameUrls(baseUrl, source.frameCount, frameStride, requestWidth) : [];
    this.#preloadFrames();
  }

  /**
   * Derive the Shopify Files base path (origin + folder, no filename, no
   * version query) from the first frame URL produced by Liquid's `file_url`.
   * An explicit override takes precedence and is used for local testing before
   * the client uploads the real frames.
   */
  #deriveBaseUrl(firstFrameUrl) {
    const override = this.#configuration.frameBaseUrlOverride;
    if (override) {
      return override.endsWith('/') ? override : `${override}/`;
    }
    if (!firstFrameUrl) return '';

    try {
      const url = new URL(firstFrameUrl, window.location.origin);
      const folderPath = url.pathname.replace(/[^/]*$/, '');
      return `${url.origin}${folderPath}`;
    } catch {
      return '';
    }
  }

  #buildFrameFilename(frameNumber) {
    const { frameName, frameSeparator, framePad, frameFormat } = this.#configuration;
    const activeName = this.#usingMobileSource ? this.#configuration.frameNameMobile : frameName;
    const numberText = framePad > 0 ? String(frameNumber).padStart(framePad, '0') : String(frameNumber);
    return `${activeName}${frameSeparator}${numberText}.${frameFormat}`;
  }

  #buildFrameUrls(baseUrl, frameCount, frameStride, requestWidth) {
    const startIndex = this.#configuration.frameStartIndex;
    const lastFrameNumber = startIndex + frameCount - 1;
    const urls = [];

    for (let frameNumber = startIndex; frameNumber <= lastFrameNumber; frameNumber += frameStride) {
      urls.push(this.#appendRequestWidth(`${baseUrl}${this.#buildFrameFilename(frameNumber)}`, requestWidth));
    }
    // Always include the final frame so the sequence ends on the intended image.
    if ((lastFrameNumber - startIndex) % frameStride !== 0) {
      urls.push(this.#appendRequestWidth(`${baseUrl}${this.#buildFrameFilename(lastFrameNumber)}`, requestWidth));
    }
    return urls;
  }

  #appendRequestWidth(url, requestWidth) {
    if (!requestWidth) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${requestWidth}`;
  }

  /* ----- Responsive sizing ----- */

  #computeRequestWidth(maxRequestWidth) {
    const devicePixelRatio = Math.min(window.devicePixelRatio || 1, this.#configuration.maxDevicePixelRatio);
    const targetWidth = (this.#canvasCssWidth || window.innerWidth) * devicePixelRatio;
    const cappedWidth = Math.min(targetWidth, maxRequestWidth);
    // Round up to the nearest 100 to maximise Shopify CDN cache hits.
    return Math.ceil(cappedWidth / 100) * 100;
  }

  #computeFrameStride(baseStride) {
    const deviceMemoryGb = navigator.deviceMemory;
    const saveDataEnabled = Boolean(navigator.connection?.saveData);

    if (saveDataEnabled) return Math.max(baseStride, this.#configuration.lowMemoryFrameStride);
    if (deviceMemoryGb && deviceMemoryGb <= this.#configuration.lowMemoryDeviceMemoryGb) {
      return Math.max(baseStride, this.#configuration.lowMemoryFrameStride);
    }
    return baseStride;
  }

  /* ----- Canvas ----- */

  /**
   * Size the canvas backing store to its CSS box. Returns true only if the
   * backing dimensions actually changed.
   *
   * Assigning canvas.width/height ALWAYS clears the canvas to transparent, even
   * when the assigned value is identical to the current one. iOS fires resize
   * repeatedly while the browser bar animates; clearing (and repainting) on every
   * one of those would flash the taupe background through — the scroll-up
   * flicker. The background element now has a stable lvh height, so its box does
   * not change as the bar moves; guarding the assignment makes those resizes a
   * no-op while still resizing on a real change (orientation, DPR).
   */
  #resizeCanvas() {
    if (!this.#canvas) return false;

    const bounds = this.getBoundingClientRect();
    this.#canvasCssWidth = bounds.width || window.innerWidth;
    this.#canvasCssHeight = bounds.height || window.innerHeight;

    const devicePixelRatio = Math.min(window.devicePixelRatio || 1, this.#configuration.maxDevicePixelRatio);
    let backingWidth = Math.round(this.#canvasCssWidth * devicePixelRatio);
    let backingHeight = Math.round(this.#canvasCssHeight * devicePixelRatio);

    // Keep the backing store under the iOS Safari canvas pixel limit.
    const pixelCount = backingWidth * backingHeight;
    if (pixelCount > this.#configuration.maxCanvasPixels) {
      const reductionScale = Math.sqrt(this.#configuration.maxCanvasPixels / pixelCount);
      backingWidth = Math.floor(backingWidth * reductionScale);
      backingHeight = Math.floor(backingHeight * reductionScale);
    }

    if (this.#canvas.width === backingWidth && this.#canvas.height === backingHeight) {
      return false;
    }

    this.#canvas.width = backingWidth;
    this.#canvas.height = backingHeight;
    return true;
  }

  /* ----- Preloading ----- */

  #preloadFrames() {
    this.#frameImages = [];
    this.#loadedFrameCount = 0;
    this.#inFlightCount = 0;

    const totalFrames = this.#frameUrls.length;
    if (totalFrames === 0) {
      this.#markReady();
      return;
    }

    this.#readyThreshold = Math.max(1, Math.ceil(totalFrames * this.#configuration.readyAtLoadedFraction));

    this.#frameUrls.forEach((url, frameIndex) => {
      const image = new Image();
      // Do NOT set image.decoding = 'async'. On WebKit (both iOS Safari and iOS
      // Chrome) an async-decoded image is frequently not yet decoded when its
      // `load` event fires, so the drawImage call in #handleFrameLoaded paints
      // nothing — yet #currentFrameIndex is still advanced, so the frame is
      // treated as drawn and never repainted. The canvas stays blank (only the
      // taupe background shows) until a later scroll happens to request a frame
      // that has since finished decoding. Leaving decoding at its default lets
      // drawImage decode synchronously on draw, which matches GSAP's canonical
      // imageSequence helper and makes the first frame paint reliably on WebKit.
      //
      // One-shot listeners with no abort signal: they must survive the element
      // being reparented (disconnected/reconnected) by GSAP's pin, otherwise the
      // first frame would never paint. They reference the stable instance.
      //
      // src is intentionally NOT assigned here. Assigning src is what starts the
      // download; #startNextLoad assigns it for a bounded number of frames at a
      // time (preloadConcurrency), nearest the on-screen frame first, so the
      // sequence is playable from the top instead of all <total> requests
      // competing at once.
      image.addEventListener('load', () => this.#handleFrameLoaded(frameIndex), { once: true });
      image.addEventListener('error', () => this.#handleFrameLoaded(frameIndex), { once: true });
      this.#frameImages[frameIndex] = image;
    });

    const concurrency = Math.min(this.#configuration.preloadConcurrency, totalFrames);
    for (let slot = 0; slot < concurrency; slot += 1) this.#startNextLoad();
  }

  /**
   * Background fill: while under preloadConcurrency, start downloading the
   * not-yet-started frame nearest the frame currently on screen
   * (#pendingFrameIndex). Called once per free slot at startup and again each
   * time a frame finishes, so the load window follows the scroll position: the
   * next frames the user will reach load first, and once the position settles
   * the window spirals outward and eventually covers every frame.
   */
  #startNextLoad() {
    if (this.#inFlightCount >= this.#configuration.preloadConcurrency) return;

    const target = this.#pendingFrameIndex;
    let nearestIndex = -1;
    let nearestDistance = Infinity;

    for (let index = 0; index < this.#frameImages.length; index += 1) {
      const image = this.#frameImages[index];
      // A non-empty src means the request has already been started (or finished).
      if (!image || image.src) continue;
      const distance = Math.abs(index - target);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
        if (distance === 0) break;
      }
    }

    if (nearestIndex === -1) return; // every frame has been started
    this.#beginLoad(nearestIndex);
  }

  /**
   * Start a frame's download immediately if it has not started yet, regardless
   * of the concurrency cap. Used by seek for the on-screen frame so a reload
   * mid-section paints the restored frame as fast as possible.
   */
  #ensureFrameLoading(frameIndex) {
    const image = this.#frameImages[frameIndex];
    if (image && !image.src) this.#beginLoad(frameIndex);
  }

  /** Kick off one frame's request and count it as in flight. */
  #beginLoad(frameIndex) {
    this.#inFlightCount += 1;
    this.#frameImages[frameIndex].src = this.#frameUrls[frameIndex];
  }

  #handleFrameLoaded(frameIndex) {
    this.#inFlightCount = Math.max(0, this.#inFlightCount - 1);
    this.#loadedFrameCount += 1;

    // Paint the frame the controller is currently waiting for the moment it
    // decodes: frame 0 at the top of the page, or the right frame when the page
    // is reloaded mid-section. No blank canvas, no wrong-frame flash.
    if (frameIndex === this.#pendingFrameIndex && frameIndex !== this.#currentFrameIndex) {
      this.#renderFrame(frameIndex);
    }
    if (!this.#isReady && this.#loadedFrameCount >= this.#readyThreshold) {
      this.#markReady();
    }

    // Free slot → pull in the next-nearest frame to the current scroll position.
    this.#startNextLoad();
  }

  #markReady() {
    this.#isReady = true;
    this.#resolveReady();
    this.dispatchEvent(new CustomEvent('frame-animation:ready', { bubbles: true }));
  }

  /* ----- Rendering ----- */

  #renderFrame(frameIndex) {
    const image = this.#frameImages[frameIndex];
    if (!this.#context || !image || !image.complete || image.naturalWidth === 0) return;

    this.#drawImageCover(image);
    this.#currentFrameIndex = frameIndex;
  }

  /** Draw an image to fill the canvas, preserving aspect ratio (object-fit: cover). */
  #drawImageCover(image) {
    const canvas = this.#canvas;
    const context = this.#context;
    if (!canvas || !context) return;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const imageWidth = image.naturalWidth;
    const imageHeight = image.naturalHeight;

    const coverScale = Math.max(canvasWidth / imageWidth, canvasHeight / imageHeight);
    const drawWidth = imageWidth * coverScale;
    const drawHeight = imageHeight * coverScale;
    const offsetX = (canvasWidth - drawWidth) / 2;
    const offsetY = (canvasHeight - drawHeight) / 2;

    // Clear before drawing. Frames with transparency (a product cut-out meant to
    // composite over the CSS backdrop image) do not overpaint the previous frame,
    // so without this they stack on top of each other as you scroll. This is what
    // GSAP's imageSequence helper does with `clear: true`. For opaque frames the
    // cover draw already repaints the whole canvas, so the clear is a harmless
    // no-op. It runs only after #renderFrame's image.complete guard, so there is
    // never a cleared-but-undrawn (blank) frame.
    context.clearRect(0, 0, canvasWidth, canvasHeight);
    context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
  }

  /* ----- Event handlers ----- */

  #handleResize = () => {
    this.redraw();
  };

  /** Rebuild and reload when crossing the desktop/mobile boundary. */
  #handleSourceChange = () => {
    if (this.#shouldUseMobileSource() === this.#usingMobileSource) return;
    this.#currentFrameIndex = -1;
    this.#initialiseActiveSource();
  };
}

if (!customElements.get('background-frame-animation')) {
  customElements.define('background-frame-animation', BackgroundFrameAnimation);
}

export { BackgroundFrameAnimation };
